import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO, isWithinInterval, startOfMonth, endOfMonth, eachMonthOfInterval, max, min, isAfter, isBefore } from 'date-fns';
import { useStyles } from "@webapps/styles";

import { Loader } from '@webapps/reactjs-tech-components';
import { CalendarProvider } from '../Stores/CalendarProvider';
import MyCalendar from './MyCalendar';
import ScheduleSliderCalendar from './ScheduleSliderCalendar';

const { NX_REST_API } = process.env;

const ScheduleCalendar = ({ onOpenAbsenceModal, setDate, date, classroomId, startDate, endDate, configurationStatus, periods }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date(date));
  const [allMonths, setAllMonths] = useState({});

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
  };

  const {
    classes,
    css,
    cx,
    theme: { spacing },
  } = useStyles(({ spacing }) => ({
    calendarWrapper: {
      backgroundColor: '#fff',
      borderRadius: '10px',
      padding: spacing(2),
    }
  }));

  // Définir le mois en fonction de la date sélectionnée ou des dates de formation
  useEffect(() => {
    if (startDate && endDate) {
      // Calculer tous les mois de l'intervalle
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const monthsInRange = eachMonthOfInterval({ start, end });
      const calculatedMonths = monthsInRange.reduce((acc, monthDate) => {
        const monthKey = format(monthDate, "MM-yyyy");
        acc[monthKey] = groupPeriodsByMonth(periods)[monthKey] || [];
        return acc;
      }, {});
      setAllMonths(calculatedMonths);

      // Prioriser la date sélectionnée si elle existe, sinon utiliser la logique par défaut
      if (date) {
        const selectedDate = parseISO(date);
        setCurrentMonth(selectedDate);
      } else {
        const today = new Date();
        if (isBefore(today, start)) {
          setCurrentMonth(start);
        } else if (isAfter(today, end)) {
          setCurrentMonth(end);
        } else {
          setCurrentMonth(today);
        }
      }
    }
  }, [startDate, endDate, periods, date]);

  useEffect(() => {
    const fetchData = async () => {
      // if (!classroomId) return;

      try {

        setIsLoading(true);

        const { data } = await axios.get(`${NX_REST_API}/schools/module/planning/holiday`);




        setHolidays(data.map(holiday => ({
          ...holiday,
          date: format(parseISO(holiday.date), 'yyyy-MM-dd')
        })));
        // setStartDate(datesRes.data.startDate);
        // setEndDate(datesRes.data.endDate);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  /**
   * Transforme un tableau de périodes en un objet organisé par mois et année.
   */
  const groupPeriodsByMonth = (periods) => {
    const grouped = {};

    periods.forEach(period => {
      const start = parseISO(period.startDate);
      const end = parseISO(period.endDate);

      // Trouver tous les mois traversés par la période
      const monthsInRange = eachMonthOfInterval({ start, end });

      monthsInRange.forEach(monthDate => {
        const monthKey = format(monthDate, "MM-yyyy");
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);

        // Déterminer les limites de la période pour ce mois
        const periodStart = max([start, monthStart]);
        const periodEnd = min([end, monthEnd]);

        // Ajouter cette période découpée au bon mois
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push({
          ...period,
          startDate: format(periodStart, "yyyy-MM-dd"),
          endDate: format(periodEnd, "yyyy-MM-dd"),
          realPeriodStartDate: format(start, "yyyy-MM-dd"),
          realPeriodEndDate: format(end, "yyyy-MM-dd"),
        });
      });
    });

    return grouped;
  };

  const filterPeriodsByMonth = (month) => {
    if (!periods.length) return [];

    return periods.filter((period) => {
      const start = parseISO(period.startDate);
      const end = parseISO(period.endDate);
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      return isWithinInterval(monthStart, { start, end }) ||
        isWithinInterval(monthEnd, { start, end }) ||
        isWithinInterval(start, { start: monthStart, end: monthEnd });
    });
  };

  function filterHolidaysByMonth(month) {
    if (!holidays.length) return [];

    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);

    return holidays.filter(holiday => {
      const holidayDate = parseISO(holiday.date);
      return isWithinInterval(holidayDate, { start: monthStart, end: monthEnd });
    });
  }

  if (isLoading) {
    return <Loader />;
  }




  return (
    <div className={classes.calendarWrapper}>
      <CalendarProvider>
        <ScheduleSliderCalendar
          date={date}
          setDate={setDate}
          month={currentMonth}
          periods={filterPeriodsByMonth(currentMonth)}
          monthPeriods={allMonths[format(currentMonth, "MM-yyyy")] || []}
          holidays={filterHolidaysByMonth(currentMonth)}
          startDate={startDate}
          endDate={endDate}
          from="school"
          enableMonthNavigation={true}
          isDashboard={true}
          onMonthChange={handleMonthChange}
          onDeclareAbsence={onOpenAbsenceModal}
          configurationStatus={configurationStatus}
          classroomId={classroomId}
          authorizeActions={{
            createPeriod: false,
          }}
          calendarStyle={css`
            width: 100% !important;
            min-width: unset !important;
            height: 100% !important;
            display: flex;
            flex-direction: column;
            padding: 0 !important;
            background: transparent !important;
          `}
        />
      </CalendarProvider>
    </div>
  );
};

export default ScheduleCalendar;
